import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

/**
 * Lazily injects the Google Maps JS SDK using the API key from the active
 * `environment.googleMapsApiKey`. Centralises script loading so:
 *   • The key isn't hard-coded in `index.html` (build-time replacement of
 *     `environment.ts` per env now controls it).
 *   • Environments with an empty key (prod / staging today) skip the load
 *     entirely instead of leaking the dev key into prod bundles.
 *   • Multiple consumers can `await load()` without re-injecting the
 *     script — the same Promise is returned on subsequent calls.
 *
 * Called from `AppComponent.ngOnInit` so the load fires at bootstrap with
 * the same `async` semantics the static `<script>` tag had previously.
 */
@Injectable({ providedIn: "root" })
export class MapsLoaderService {
  private static readonly SCRIPT_ID = "google-maps-sdk";

  private loadPromise: Promise<void> | null = null;

  load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    const key = environment.googleMapsApiKey;
    // No key configured for this build (prod/staging today) — resolve as a
    // no-op so consumers that gracefully handle a missing `google.maps`
    // global continue to work without errors.
    if (!key) {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    // Defensive: if some other code path (legacy `<script>` tag, hot
    // reload, two simultaneous bootstraps) already injected the SDK,
    // treat it as loaded.
    if (document.getElementById(MapsLoaderService.SCRIPT_ID)) {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.id = MapsLoaderService.SCRIPT_ID;
      // `loading=async` is the URL flag the loader inspects to suppress
      // the "loaded synchronously" performance warning — the `async`
      // attribute alone isn't enough.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        key,
      )}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load Google Maps JS SDK"));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }
}
